/**
 * Client-side data collection for form submissions
 *
 * Collects browser context, cookies, URL parameters, and analytics IDs.
 */
import type { ClientData, ClientDataContextProps } from '../core';

const SOFTWARE_CATEGORY_REGEX = /(.+?)\s+software$/i;

// Type for window properties used by bx-forms (avoids global declaration conflicts)
interface BxFormsWindowExtensions {
  // biome-ignore lint/suspicious/noExplicitAny: dataLayer items have various shapes
  dataLayer?: any[];
  initialState?: { appType: string; formId: string };
  SSR_BRIDGE_DATA?: {
    pageData: { categoryData: { longName: string } };
    category: { longName: string };
  };
}

// Helper to access window with bx-forms extensions
const getExtendedWindow = (): (Window & BxFormsWindowExtensions) | undefined =>
  typeof window !== 'undefined'
    ? (window as Window & BxFormsWindowExtensions)
    : undefined;

/**
 * Get cookie value by name
 */
export const getCookie = (name: string): string => {
  if (typeof window === 'undefined') return '';
  const value = `; ${window.parent.document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || '';
  }
  return '';
};

/**
 * Extract URL data from the parent window
 */
export const getDataFromUrl = () => {
  if (typeof window === 'undefined') {
    return {
      url: '',
      pageUrl: '',
      pageTitle: '',
      utmSource: '',
      utmMedium: '',
      utmCampaign: '',
      utmContent: '',
      utmTerm: '',
    };
  }

  const _window = window.parent;
  const href = _window.location.href;
  const pageUrl = href.split('?')[0];
  const queryString = _window.location.search;
  const urlParams = new URLSearchParams(queryString);

  return {
    url: href,
    pageUrl: pageUrl,
    pageTitle: _window.document.title,
    utmSource: urlParams.get('utm_source') || '',
    utmMedium: urlParams.get('utm_medium') || '',
    utmCampaign: urlParams.get('utm_campaign') || '',
    utmContent: urlParams.get('utm_content') || '',
    utmTerm: urlParams.get('utm_term') || '',
  };
};

/**
 * Get client-side tracking fields
 */
export const getClientFields = (
  formId: string,
  contextProps?: ClientDataContextProps
) => {
  if (typeof window === 'undefined') {
    return {
      url: '',
      pageUrl: '',
      pageTitle: '',
      utmSource: '',
      utmMedium: '',
      utmCampaign: '',
      utmContent: '',
      utmTerm: '',
      isBeta: false,
      contentfulFormId: formId,
      googleId: '',
      lyticsId: '',
      adobeId: '',
      uwId: '',
    };
  }

  const _window = window.parent as Window & BxFormsWindowExtensions;
  const dataUrl = getDataFromUrl();

  // Try to get Google Analytics ID from cookie
  const googleId = getCookie('_ga');

  // Try to get Lytics ID from cookie or dataLayer
  const lyticsId =
    getCookie('seerid') ||
    ((_window.dataLayer || []).find(
      (x: Record<string, unknown>) => x.category === 'lytics'
    )?.userID as string) ||
    '';

  // Get Adobe ECID from dataLayer
  const adobeId =
    ((_window.dataLayer || []).find(
      (x: Record<string, unknown>) => x.event === 'setEcid'
    )?.ecid as string) || '';

  // Get UserWay ID from dataLayer
  const uwId =
    ((_window.dataLayer || []).find(
      (x: Record<string, unknown>) => x.event === 'uwLoggedIn'
    )?.userId as string) || '';

  // Determine beta flag from contextProps (no data attributes available)
  const isBeta = contextProps?.isBeta ?? false;

  const extWindow = getExtendedWindow();
  return {
    ...dataUrl,
    isBeta,
    contentfulFormId: extWindow?.initialState?.formId || formId,
    googleId,
    lyticsId,
    adobeId,
    uwId,
  };
};

/**
 * Get page-level parameters
 * Priority: contextProps > defaults
 */
export const getPageParams = (
  _formId: string,
  contextProps?: ClientDataContextProps
) => {
  return {
    pageVariant: contextProps?.pageVariant ?? '',
    pageType: contextProps?.pageType ?? '',
    pageCategory: contextProps?.pageCategory ?? getPageCategoryDefaultValue(),
    pagePlacement: contextProps?.pagePlacement ?? 'inline',
  };
};

/**
 * Get context parameters (category and product)
 */
export const getContextParams = (
  _formId: string,
  contextProps?: ClientDataContextProps
) => {
  return {
    contextCategory: contextProps?.contextCategory ?? '',
    contextProduct: contextProps?.contextProduct ?? '',
  };
};

/**
 * Get product parameters
 */
export const getProductParams = (
  _formId: string,
  contextProps?: ClientDataContextProps
) => {
  return {
    productId: contextProps?.productId ?? '',
    productUrl: contextProps?.productUrl ?? '',
  };
};

/**
 * Helper function to convert dash-case to Title Case
 */
const dashCaseToTitleCase = (string: string) =>
  string
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.substr(1))
    .join(' ');

/**
 * Get Next.js props from the page
 */
const getNextJsProps = (): Record<string, unknown> | null => {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(
      document.getElementById('__NEXT_DATA__')?.innerHTML || '{}'
    )?.props;
  } catch {
    return null;
  }
};

/**
 * Get category default value from services page
 */
const getServicesPageCategoryDefaultValue = () => {
  const props = getNextJsProps();
  const pageProps = props?.pageProps as Record<string, unknown> | undefined;
  const value = pageProps?.categoryId as string | undefined;
  if (value) {
    return dashCaseToTitleCase(value);
  }
  return undefined;
};

/**
 * Get Software Advice category name
 */
const getSoftwareAdviceCategoryName = () => {
  const props = getNextJsProps();
  const pageProps = props?.pageProps as Record<string, unknown> | undefined;
  const category = pageProps?.category as Record<string, unknown> | undefined;
  const value = category?.title_h1 as string | undefined;
  if (value) {
    // Normalize the Software Advice categories to match the Capterra names
    const matches = value.match(SOFTWARE_CATEGORY_REGEX);
    return matches ? matches[1] : undefined;
  }
  return undefined;
};

/**
 * Get page category default value from various sources
 */
export const getPageCategoryDefaultValue = () => {
  const extWindow = getExtendedWindow();
  if (!extWindow) return '';

  const props = getNextJsProps();
  const pageProps = props?.pageProps as Record<string, unknown> | undefined;
  const post = pageProps?.post as Record<string, unknown> | undefined;
  const featuredSoftwareCategory = post?.featuredSoftwareCategory as
    | Record<string, unknown>
    | undefined;
  const appData = pageProps?.appData as Record<string, unknown> | undefined;
  const appCategory = appData?.category as Record<string, unknown> | undefined;

  return (
    extWindow.SSR_BRIDGE_DATA?.pageData?.categoryData?.longName ||
    extWindow.SSR_BRIDGE_DATA?.category?.longName ||
    getServicesPageCategoryDefaultValue() ||
    (featuredSoftwareCategory?.name as string) ||
    getSoftwareAdviceCategoryName() ||
    (appCategory?.name as string) ||
    ''
  );
};

/**
 * Collect all client-side data for form submission
 */
export const collectClientData = (
  formId: string,
  contextProps?: ClientDataContextProps
): ClientData => {
  const clientFields = getClientFields(formId, contextProps);
  const pageParams = getPageParams(formId, contextProps);
  const contextParams = getContextParams(formId, contextProps);
  const productParams = getProductParams(formId, contextProps);

  return {
    ...clientFields,
    ...pageParams,
    ...contextParams,
    ...productParams,
  };
};
